import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, AlertTriangle, AlertCircle, Info, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { notificationsAPI } from '../../lib/api';

const getSeverityStyles = (severity) => {
  switch (severity) {
    case 'critical':
      return {
        bg: 'bg-red-50 border-red-200',
        icon: <AlertTriangle className="h-4 w-4 text-red-600" />,
        badge: 'bg-red-100 text-red-800'
      };
    case 'warning':
      return {
        bg: 'bg-yellow-50 border-yellow-200',
        icon: <AlertCircle className="h-4 w-4 text-yellow-600" />,
        badge: 'bg-yellow-100 text-yellow-800'
      };
    case 'info':
    default:
      return {
        bg: 'bg-blue-50 border-blue-200',
        icon: <Info className="h-4 w-4 text-blue-600" />,
        badge: 'bg-blue-100 text-blue-800'
      };
  }
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await notificationsAPI.dismiss(notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.case_id) {
      navigate('/case-health');
    }
    setOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.read_at && !n.dismissed_at).length;
  const activeNotifications = notifications.filter(n => !n.dismissed_at);
  const criticalCount = activeNotifications.filter(n => n.severity === 'critical').length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative h-9 w-9 p-0"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${criticalCount > 0 ? 'text-red-600' : 'text-slate-600'}`} />
          {unreadCount > 0 && (
            <span 
              className={`absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                criticalCount > 0 ? 'bg-red-600 animate-pulse' : 'bg-blue-600'
              }`}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-slate-600" />
            <span className="font-semibold text-slate-900">Notifications</span>
            {criticalCount > 0 && (
              <Badge className="bg-red-100 text-red-800 text-xs">
                {criticalCount} critical
              </Badge>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs text-blue-600 hover:text-blue-700 h-auto p-1"
            onClick={() => { 
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View All Notifications
          </Button>
        </div>
        <ScrollArea className="h-[300px]">
          {loading && activeNotifications.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              Loading...
            </div>
          ) : activeNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <Check className="h-10 w-10 mx-auto text-green-500 mb-2" />
              <p className="text-sm text-slate-500">All caught up!</p>
              <p className="text-xs text-slate-400 mt-1">No active alerts</p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {activeNotifications.slice(0, 10).map((notification) => {
                const styles = getSeverityStyles(notification.severity);
                return (
                  <div
                    key={notification.notification_id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors hover:opacity-90 ${styles.bg}`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">{styles.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm text-slate-900 truncate">
                            {notification.title}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 shrink-0 hover:bg-slate-200"
                            onClick={(e) => handleDismiss(e, notification.notification_id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                          {notification.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="outline" className={`text-xs ${styles.badge}`}>
                            {notification.severity}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        {activeNotifications.length > 0 && (
          <div className="p-2 border-t border-slate-200">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              onClick={() => { 
                setOpen(false);
                navigate('/notifications');
              }}
            >
              View All Notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
